import { useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  LabelList,
  Label
} from 'recharts';

const Intereses = () => {
  const [timeRange, setTimeRange] = useState('30D');
  const [dateRange] = useState('Jan 01, 2025 - Feb 20, 2025');

  const areasInteres = [
    { name: 'Marketing Digital', value: 30, color: '#024579' },
    { name: 'Desarrollo Web', value: 25, color: '#028bbf' },
    { name: 'Diseño UX/UI', value: 20, color: '#00bf63' },
    { name: 'Data Science', value: 15, color: '#024579' },
    { name: 'Gestión de Proyectos', value: 10, color: '#028bbf' }
  ];

  const tendenciasIndustria = [
    { mes: 'Ene', tecnologia: 45, marketing: 30, diseno: 25 },
    { mes: 'Feb', tecnologia: 48, marketing: 32, diseno: 28 },
    { mes: 'Mar', tecnologia: 52, marketing: 35, diseno: 30 },
    { mes: 'Abr', tecnologia: 55, marketing: 38, diseno: 32 },
    { mes: 'May', tecnologia: 58, marketing: 40, diseno: 35 },
    { mes: 'Jun', tecnologia: 60, marketing: 42, diseno: 38 }
  ];

  const habilidadesRequeridas = [
    { habilidad: 'Programación', demanda: 85 },
    { habilidad: 'Análisis de datos', demanda: 78 },
    { habilidad: 'Diseño gráfico', demanda: 72 },
    { habilidad: 'Marketing digital', demanda: 68 },
    { habilidad: 'Gestión de proyectos', demanda: 65 }
  ].sort((a, b) => b.demanda - a.demanda);

  const puestosPostulados = [
    { puesto: 'Diseñador UX/UI', cantidad: 85 },
    { puesto: 'Desarrollador Frontend', cantidad: 78 },
    { puesto: 'Marketing Digital', cantidad: 72 },
    { puesto: 'Product Manager', cantidad: 65 },
    { puesto: 'Data Analyst', cantidad: 60 }
  ].sort((a, b) => b.cantidad - a.cantidad);

  const empresasPopulares = [
    { empresa: 'BCP', popularidad: 92, logo: '🏦' },
    { empresa: 'BBVA', popularidad: 88, logo: '🏦' },
    { empresa: 'McKinsey', popularidad: 85, logo: '🏢' },
    { empresa: 'Alicorp', popularidad: 82, logo: '🏭' },
    { empresa: 'AJE', popularidad: 80, logo: '🏭' }
  ].sort((a, b) => b.popularidad - a.popularidad);

  const postulacionesPorCarrera = [
    { carrera: 'Ciencias de la Comunicación', postulaciones: 245, tasa: 75 },
    { carrera: 'Diseño y Comunicación Visual', postulaciones: 210, tasa: 68 },
    { carrera: 'Marketing', postulaciones: 198, tasa: 72 },
    { carrera: 'Administración', postulaciones: 185, tasa: 65 },
    { carrera: 'Arquitectura', postulaciones: 165, tasa: 70 }
  ].sort((a, b) => b.postulaciones - a.postulaciones);

  const impactoLaboral = [
    { mes: 'Ene', entrevistas: 45, contrataciones: 22 },
    { mes: 'Feb', entrevistas: 52, contrataciones: 25 },
    { mes: 'Mar', entrevistas: 58, contrataciones: 30 },
    { mes: 'Abr', entrevistas: 65, contrataciones: 35 },
    { mes: 'May', entrevistas: 72, contrataciones: 42 },
    { mes: 'Jun', entrevistas: 78, contrataciones: 48 }
  ];

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={16}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Intereses Profesionales</h1>
          <p className="mt-1 text-sm text-gray-500">
            Análisis de tendencias e intereses profesionales de los estudiantes
          </p>
          <p className="mt-1 text-sm text-gray-400">{dateRange}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setTimeRange('7D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '7D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            7D
          </button>
          <button
            onClick={() => setTimeRange('30D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '30D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            30D
          </button>
          <button
            onClick={() => setTimeRange('90D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '90D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            90D
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Primera fila: Distribución y Tendencias */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Distribución de Áreas de Interés */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Distribución de Áreas de Interés</h2>
            <div style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={areasInteres}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={150}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {areasInteres.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tendencias por Industria */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Tendencias por Industria</h2>
            <div style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={tendenciasIndustria}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="tecnologia"
                    name="Tecnología"
                    stroke="#024579"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="marketing"
                    name="Marketing"
                    stroke="#028bbf"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="diseno"
                    name="Diseño"
                    stroke="#00bf63"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Segunda fila: Habilidades y Empresas */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Habilidades más Requeridas */}
          <div className="bg-white shadow rounded-lg p-6 lg:col-span-2">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Habilidades más Requeridas</h2>
            <div style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={habilidadesRequeridas}
                  margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="habilidad" type="category" />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="demanda"
                    name="Demanda del Mercado"
                    fill="#024579"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Ranking de Empresas más Populares */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Ranking de Empresas más Populares</h2>
            <div className="space-y-6">
              {empresasPopulares.map((empresa, index) => (
                <div key={empresa.empresa} className="relative">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <span className="text-lg font-bold mr-3" style={{ color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#024579' }}>
                        #{index + 1}
                      </span>
                      <span className="text-2xl mr-2">{empresa.logo}</span>
                      <span className="text-sm font-medium text-gray-900">{empresa.empresa}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-500">{empresa.popularidad}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${empresa.popularidad}%`,
                        backgroundColor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#024579'
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tercera fila: Puestos más Postulados y Postulaciones por Carrera */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Puestos más Postulados */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Puestos más Postulados</h2>
            <div style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={puestosPostulados}
                  margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="puesto" type="category" />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="cantidad"
                    name="Cantidad de Postulaciones"
                    fill="#00bf63"
                    radius={[0, 4, 4, 0]}
                  >
                    <LabelList
                      dataKey="cantidad"
                      position="right"
                      formatter={(value) => `${value}%`}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Postulaciones por Carrera */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Postulaciones por Carrera</h2>
            <div style={{ height: '400px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={postulacionesPorCarrera}
                  margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="carrera" 
                    type="category"
                    width={150}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'postulaciones' ? `${value} postulaciones` : `${value}% tasa de éxito`,
                      name === 'postulaciones' ? 'Total' : 'Tasa de Éxito'
                    ]}
                  />
                  <Legend />
                  <Bar
                    dataKey="postulaciones"
                    name="Total de Postulaciones"
                    fill="#024579"
                    radius={[0, 4, 4, 0]}
                  >
                    <LabelList
                      dataKey="postulaciones"
                      position="right"
                    />
                  </Bar>
                  <Bar
                    dataKey="tasa"
                    name="Tasa de Éxito"
                    fill="#00bf63"
                    radius={[0, 4, 4, 0]}
                  >
                    <LabelList
                      dataKey="tasa"
                      position="right"
                      formatter={(value) => `${value}%`}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tasa de Impacto Laboral */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Tasa de Impacto Laboral - El Chambas</h2>
              <p className="mt-1 text-sm text-gray-500">
                % de estudiantes que accedieron a entrevistas o fueron contratados gracias a oportunidades enviadas por El Chambas
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <span className="h-3 w-3 rounded-full bg-[#024579] mr-2"></span>
                <span className="text-sm text-gray-600">Entrevistas</span>
              </div>
              <div className="flex items-center">
                <span className="h-3 w-3 rounded-full bg-[#00bf63] mr-2"></span>
                <span className="text-sm text-gray-600">Contrataciones</span>
              </div>
            </div>
          </div>
          <div style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={impactoLaboral}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="mes"
                  padding={{ left: 30, right: 30 }}
                />
                <YAxis>
                  <Label
                    value="Porcentaje de estudiantes"
                    angle={-90}
                    position="insideLeft"
                    style={{ textAnchor: 'middle' }}
                  />
                </YAxis>
                <Tooltip
                  formatter={(value) => [`${value}%`, '']}
                  labelStyle={{ color: '#111827' }}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    padding: '8px'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="entrevistas"
                  name="Tasa de Entrevistas"
                  stroke="#024579"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                >
                  <LabelList
                    dataKey="entrevistas"
                    position="top"
                    formatter={(value) => `${value}%`}
                  />
                </Line>
                <Line
                  type="monotone"
                  dataKey="contrataciones"
                  name="Tasa de Contratación"
                  stroke="#00bf63"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  activeDot={{ r: 8 }}
                >
                  <LabelList
                    dataKey="contrataciones"
                    position="bottom"
                    formatter={(value) => `${value}%`}
                  />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500">Promedio de Entrevistas</h3>
              <p className="mt-2 text-3xl font-bold text-[#024579]">
                {Math.round(impactoLaboral.reduce((acc, curr) => acc + curr.entrevistas, 0) / impactoLaboral.length)}%
              </p>
              <p className="mt-1 text-sm text-gray-500">de los estudiantes</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500">Promedio de Contrataciones</h3>
              <p className="mt-2 text-3xl font-bold text-[#00bf63]">
                {Math.round(impactoLaboral.reduce((acc, curr) => acc + curr.contrataciones, 0) / impactoLaboral.length)}%
              </p>
              <p className="mt-1 text-sm text-gray-500">de los estudiantes</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Intereses; 